/**
 * Manage component (manage.component.ts).
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @author Jenny Lam <jlam@buildcentrix.com>
 * @copyright (c) 2021-2024 Build Centrix
 */
import {
  ChangeDetectorRef,
  Component,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { NgForm } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ManageAttachmentsComponent } from '../../../../components/modals/order-pending-attachments/manage-attachments.component';
import { WdOrderShippingOptionsService } from '../../../../services/orders/options/shipping/wd-order-shipping-options.service';
import { WdOrderStatusService } from '../../../../services/orders/statuses/wd-order-status.service';
import { WdLocalSharedFunctionsService } from '../../../../services/shared/wd-local-shared-functions.service';
import { WdOrdersService } from '../../../../services/orders/wd-orders.service';
import { WdLocalEnvironmentService } from '../../../../services/environment/wd-local-environment.service';
import { WdOrderMessageService } from '../../../../services/orders/messages/wd-order-message.service';
import { WdJobService } from '../../../../services/jobs/wd-jobs.service';
import { MatDialog } from '@angular/material/dialog';
import { WdMediaUploadService } from '../../../../services/media/wd-media-upload.service';
import { OrderAdjustmentsComponent } from '../../../../components/modals/order-adjustments/order-adjustments.component';
import { WdOrderAdjustmentsService } from '../../../../services/orders/adjustments/wd-order-adjustments.service';
import { WdOrderShippingGeolocationsService } from '../../../../services/orders/options/geolocations/wd-order-shipping-geolocations.service';
import { WdOrderShippingLoadingZonesService } from '../../../../services/orders/options/shipping/wd-order-shipping-loading-zones.service';
import { ActivatedRoute } from '@angular/router';
import { WdOrdersV2Service } from '../../../../services/orders/wd-orders-v2.service';
import { WdManageOrdersService } from '../../../../services/orders/manage/wd-manage-orders.service';
import {
  IOrderFormFieldsModel,
  IOrderFormModel,
  IOrderSections,
  IRawInputOrder,
  OrderShippingOptions,
} from '../../../../interfaces/orders/order-interfaces';
import {
  IBcxGeolocationModel,
  IBcxErrorService,
  IBcxSharedFunctionsService,
} from '../../../../interfaces/generic/generic-interfaces';
import { WdUserService } from '../../../../services/users/wd-users.service';
import { WdVehicleAssignmentService } from '../../../../services/vehicles/assignments/wd-vehicle-assignment.service';
import { OrderAssignmentsComponent } from '../../../../components/modals/order-assignments/order-assignments.component';
import { OrderItemHolesComponent } from 'src/app/components/modals/order-item-holes/order-item-holes.component';
import { WdOrderItemHolesService } from 'src/app/services/orders/holes/wd-order-item-holes.service';

@Component({
  selector: 'app-orders-manage-component',
  templateUrl: './manage.component.html',
  styleUrls: ['../../orders.component.scss', './manage.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ManageComponent implements OnInit, OnDestroy {
  /**
   * Constructor Initialization
   */
  constructor(
    private matDialog: MatDialog,
    private ngZone: NgZone,
    private wdTranslate: TranslateService,
    public changeDetector: ChangeDetectorRef,
    public viewportScroller: ViewportScroller,
    public wdEnvironmentService: WdLocalEnvironmentService,
    public wdFileUploadService: WdMediaUploadService,
    public wdJobService: WdJobService,
    public wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    public wdManageOrdersService: WdManageOrdersService,
    public wdOrdersService: WdOrdersService,
    public wdOrdersV2Service: WdOrdersV2Service,
    public wdOrderAdjustmentsService: WdOrderAdjustmentsService,
    public wdOrderMessageService: WdOrderMessageService,
    public wdOrderShippingGeolocationsService: WdOrderShippingGeolocationsService,
    public wdOrderShippingLoadingZonesService: WdOrderShippingLoadingZonesService,
    public wdOrderShippingOptionsService: WdOrderShippingOptionsService,
    public wdVehicleAssignmentService: WdVehicleAssignmentService,
    public wdOrderStatusService: WdOrderStatusService,
    public wdUsersService: WdUserService,
    public wdOrderItemHolesService: WdOrderItemHolesService,
    public activatedRoute: ActivatedRoute,
    @Inject('wdErrorService') public wdErrorService: IBcxErrorService,
    @Inject('wdSharedFunctionsService')
    public wdSharedFunctionsService: IBcxSharedFunctionsService,
  ) {}

  orderIndex = '';

  // Basic Variables
  orderTitle = '';
  orderSubtitle = '';

  // Order Items
  @ViewChild(MatSort, { static: true }) orderOrders!: MatSort;
  @ViewChild(MatSort, { static: true }) orderProducts!: MatSort;

  // Order Items Columns
  orderAdjustmentColumns = [
    'label',
    'description',
    'fabrication',
    'weight',
    'cost',
    'price',
    'material',
    'gauge',
    'edit',
    'delete',
  ];
  // Order Items Columns
  orderFittingColumns = [
    'select',
    'label',
    'dimensions',
    'joints',
    'other',
    'notes',
    'status',
  ];
  // Order Items Columns
  orderProductColumns = [
    'select',
    'label',
    'attributes',
    'other',
    'notes',
    'status',
  ];
  // Tracker Items Columns
  trackerItemColumns = ['label', 'notes', 'tag'];

  // Form
  @ViewChild('datesForm', { static: true })
  public datesForm!: NgForm;
  @ViewChild('orderDetailsForm', { static: false })
  public orderDetailsForm!: NgForm;
  @ViewChild('jobForm', { static: true })
  public jobForm!: NgForm;
  @ViewChild('specialInstructionsForm', { static: true })
  public specialInstructionsForm!: NgForm;
  @ViewChild('statusesForm', { static: true })
  public statusesForm!: NgForm;
  @ViewChild('shippingDetailsForm', { static: true })
  public shippingDetailsForm!: NgForm;
  @ViewChild('formFieldsForm', { static: true })
  public formFieldsForm!: NgForm;

  // Subscriptions
  subscriptions = new Subscription();

  // Variables
  jobTypeaheadResults = new BehaviorSubject<Array<object>>([]);
  phaseTypeaheadResults = new BehaviorSubject<Array<object>>([]);

  // Current Order
  order: any;

  // Booleans
  isEdit = false;
  isLoading = false;
  searchingPhases = false;

  // Sections Booleans
  orderState: IOrderSections = {
    attachments: {},
    dates: {},
    details: {},
    formFields: {},
    instructions: {},
    job: {},
    massages: {},
    delivery: {},
    statuses: {},
  };

  // Form
  orderFormModel: IOrderFormModel = {};

  // Pending Files
  uploadQueue: string[] = [];

  // Geolocation options
  countryDropdownOptions: Array<IBcxGeolocationModel> = [];
  stateDropdownOptions: Array<IBcxGeolocationModel> = [];

  // Active fulfillment group indexes
  activeFulfillmentGroupIndexes: string[] = [];

  isProductionDateRangeValid = true;
  isProductionEndBeforeShipping = true;
  isDeliveryWindowValid = true;
  showShippingDetailsContainer = false; // If true then show the shipping address details container box

  // Enums
  readonly orderShippingOptions = OrderShippingOptions;

  /**
   * Get Job Details by Key
   * @since 4.35.0
   */
  static getJobDetails(job: any, key: string) {
    let result = Object.prototype.hasOwnProperty.call(job, key)
      ? job[key]
      : null;

    if (job.override) {
      result = Object.prototype.hasOwnProperty.call(job.override, key)
        ? job.override[key]
        : null;
    }

    return result;
  }

  /**
   * Returns true if the order has E-commerce job override
   * @since 4.47.0
   */
  hasEcomJobOverride() {
    return (
      this.orderFormModel.isEcommerce &&
      this.orderFormModel[this.wdLocalSharedFunctions.orderSectionType.JOB].job
        .override
    );
  }

  /**
   * Delete Adjustment
   * @since 4.36.0
   */
  async deleteAdjustment(adjustment: any): Promise<any> {
    // Start Loader
    adjustment.isLoading = true;
    try {
      // if the adjustment is added via core-api then it would have an integer index,
      // if the adjustment is added via order-api then it would have a 24 character Object ID
      const id = adjustment.id ? adjustment.id : adjustment.index;
      return await this.wdOrderAdjustmentsService.deleteOrderAdjustment(
        this.orderIndex,
        id,
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Reload Order Data
      void this.loadOrder();
      // Stop Loader
      adjustment.isLoading = false;
    }
  }

  /**
   * Job Model Changes
   * @since 4.52.0
   */
  async jobChange(model: any, form: any) {
    // Check for Value Change
    this.checkForValueChanges(
      this.wdLocalSharedFunctions.orderSectionType.JOB,
      form,
    );
    // Set Phases
    void this.jobPhasesSearch(model);
  }

  /**
   * Job Phase Search Changes
   * @since 4.36.0
   */
  async jobPhasesSearch(job?: any) {
    if (job && job.index) {
      // Start Loader
      this.searchingPhases = true;
      // Phases
      let phases: Array<any> = [];
      if (job && job.index) {
        phases = await this.wdJobService.getPhasesByIndex(job.index);
      } else {
        // If no job, clear the current phase
        this.orderFormModel[
          this.wdLocalSharedFunctions.orderSectionType.JOB
        ].phase = null;
      }
      // Set phases
      this.phaseTypeaheadResults.next(phases);
      // Stop Loader
      this.searchingPhases = false;
      // Detect Changes
      this.changeDetector.detectChanges();
    } else {
      // Clear phase / phase options
      this.orderFormModel[
        this.wdLocalSharedFunctions.orderSectionType.JOB
      ].phase = null;
      this.phaseTypeaheadResults.next([]);
    }
  }

  /**
   * Fetch order item shipping statuses from the API
   * @since 4.26.0
   */
  async getOrderItemShippingStatuses(): Promise<any> {
    // Start Loader
    this.isLoading = true;
    try {
      return await this.wdOrderStatusService.getOrderItemShippingStatuses();
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Stop Loader
      this.isLoading = false;
    }
  }

  /**
   * Fetch shipping loading zone options from the API
   * @since 4.52.0
   */
  async getOrderShippingLoadingZones(): Promise<void> {
    // Start Loader
    this.isLoading = true;
    try {
      await this.wdOrderShippingLoadingZonesService.getLoadingZoneOptions();
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Stop Loader
      this.isLoading = false;
    }
  }

  /**
   * Fetch shipping vehicle assignment options from the API.
   */
  async getOrderShippingVehicleAssignments(): Promise<void> {
    // Start Loader
    this.isLoading = true;
    try {
      await this.wdVehicleAssignmentService.fetchVehicleAssignments(
        true,
        false,
        false,
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Stop Loader
      this.isLoading = false;
    }
  }

  /**
   * Fetch overall order statuses from the API
   * @since 4.26.0
   */
  async getOverallOrderStatuses(): Promise<any> {
    // Start Loader
    this.isLoading = true;
    try {
      return await this.wdOrderStatusService.getOrderStatuses();
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Stop Loader
      this.isLoading = false;
    }
  }

  /**
   * Fetch order production statuses from the API
   * @since 4.26.0
   */
  async getProductionStatuses(): Promise<any> {
    // Start Loader
    this.isLoading = true;
    try {
      return await this.wdOrderStatusService.getProductionStatuses();
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Stop Loader
      this.isLoading = false;
    }
  }

  /**
   * Fetch order shipping statuses from the API
   * @since 4.26.0
   */
  async getShippingStatuses(): Promise<any> {
    // Start Loader
    this.isLoading = true;
    try {
      return await this.wdOrderStatusService.getShippingStatuses();
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Stop Loader
      this.isLoading = false;
    }
  }

  /**
   * Fetch order shipping options from the API
   * @since 4.26.0
   */
  async getShippingOptions(): Promise<any> {
    // Start Loader
    this.isLoading = true;
    try {
      return await this.wdOrderShippingOptionsService.getShippingOptions();
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Stop Loader
      this.isLoading = false;
    }
  }

  /**
   * Fetch order geolocation options from the service
   * @since 4.41.0
   */
  async getGeolocationOptions(): Promise<any> {
    // Start Loader
    this.isLoading = true;
    try {
      this.countryDropdownOptions = await this.wdOrderShippingGeolocationsService.getGeolocationOptions();
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Stop Loader
      this.isLoading = false;
    }
  }

  /**
   * Add new vehicle assignment for order id and date.
   */
  addVehicleAssignmentForOrder(orderFormModel: IOrderFormModel) {
    this.wdVehicleAssignmentService
      .addOrderVehicleAssignment(
        orderFormModel.details.id,
        orderFormModel.dates.delivery,
      )
      .then(async (result) => {
        if (result) {
          // Reload vehicle assignments
          await this.getOrderShippingVehicleAssignments();
          // Reload order data
          void this.loadOrder();
        }
      });
  }

  /**
   * Load Order
   * @since 4.35.0
   */
  async loadOrder(showLoader = false) {
    this.orderTitle = 'Manage Order';

    // Load Order
    if (this.orderIndex) {
      this.isEdit = true;
      const order: any = await this.wdManageOrdersService.getOrderByIndex(
        this.orderIndex,
        showLoader,
      );
      // Set Order Form Model
      this.orderFormModel = this.initializeOrderFormModel(order);
      // Set active fulfillment group indexes
      this.setActiveFulfillmentGroupIndexes(order);
      // Set up the state options
      this.setStateOptions();
      // Set Titles
      const jobNumber = `[${ManageComponent.getJobDetails(
        this.orderFormModel[this.wdLocalSharedFunctions.orderSectionType.JOB]
          .job,
        'number',
      )}]`;
      const jobName = ` ${ManageComponent.getJobDetails(
        this.orderFormModel[this.wdLocalSharedFunctions.orderSectionType.JOB]
          .job,
        'name',
      )}`;
      this.orderTitle =
        'Order #' +
        this.orderFormModel[
          this.wdLocalSharedFunctions.orderSectionType.DETAILS
        ].number;
      this.orderSubtitle = jobNumber + jobName;
    }
  }

  /**
   * Set default values for order manage form
   */
  async setData(): Promise<void> {
    // Start Loader
    this.isLoading = true;

    // Reset Form Validity
    this.datesForm?.reset();
    this.orderDetailsForm?.reset();
    this.jobForm?.reset();
    this.specialInstructionsForm?.reset();
    this.statusesForm?.reset();
    this.shippingDetailsForm?.reset();
    this.formFieldsForm?.reset();

    // Get Status / Shipping Options
    await this.getOrderItemShippingStatuses();
    await this.getProductionStatuses();
    await this.getShippingStatuses();
    await this.getShippingOptions();
    await this.getGeolocationOptions();
    await this.getOrderShippingLoadingZones();
    // Assigned vehicles are restricted to order admins only
    if (this.wdEnvironmentService.permissions.canViewVehicleAssignments) {
      await this.getOrderShippingVehicleAssignments();
    }
    // Order statuses are restricted to order admins only
    if (this.wdEnvironmentService.permissions.canChangeOrderStatus) {
      await this.getOverallOrderStatuses();
    }

    // Load Order
    await this.loadOrder(true);

    // Set Window Title
    this.wdSharedFunctionsService.setWindowTitle(
      this.orderTitle +
        ' - ' +
        this.wdEnvironmentService.constants.getCompanyName,
    );

    // Begin Detecting Model Changes
    // https://stackoverflow.com/questions/51455545/when-to-use-ngzone-run
    this.ngZone.run(() => {
      // Stop Loader
      this.isLoading = false;
    });
  }

  /**
   * Update order status
   * @since 4.35.0
   */
  async updateFulfillmentGroupStatus(
    orderIndex: any,
    orderGroup: any,
  ): Promise<void> {
    // Start Loader
    orderGroup.isUpdating = true;
    try {
      // Update Status
      await this.wdOrdersV2Service.updateFulfillmentGroupStatus(
        orderIndex,
        orderGroup?.index,
        orderGroup?.status?.id,
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Reload Order Data
      void this.loadOrder();
      // Stop Loader
      orderGroup.isUpdating = false;
    }
  }

  /**
   * Update order item shipping status
   * @since 4.35.0
   */
  async updateOrderItemShippingStatus(
    id: string,
    itemId: string,
    status: any,
  ): Promise<void> {
    // Start Loader
    status.isUpdating = true;
    try {
      // Update Status
      await this.wdOrdersService.updateOrderItemShippingStatuses(
        id,
        [itemId],
        status.id,
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Reload Order Data
      void this.loadOrder();
      // Start Loader
      status.isUpdating = false;
    }
  }

  /**
   * Update order item shipping status
   * @since 4.35.0
   */
  async updateSelectedOrderItemShippingStatuses(
    id: string,
    group: any,
    status: any,
    key: string,
  ): Promise<void> {
    // Start Loader
    group[key + 'Updating'] = true;
    try {
      // Items
      const items = group[key + 'Selection'].selected;
      // Update Status
      await this.wdOrdersService.updateOrderItemShippingStatuses(
        id,
        items.map((item: any) => item.id),
        status.id,
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
    } finally {
      // Clear Selection
      group[key + 'Selection'].clear();
      // Reload Order Data
      void this.loadOrder();
      // Stop Loader
      group[key + 'Updating'] = false;
    }
  }

  /**
   * Check All Values for Changes
   * @since 4.36.0
   */
  checkForValueChanges(key: string, form?: any) {
    // Check for changes
    this.orderState[key].isChanges =
      this.orderState[key].beforeChanges !==
      JSON.stringify(this.orderFormModel[key]);
    // Typeaheads don't implicitly set form to dirty
    form?.markAsDirty();

    if (key === this.wdLocalSharedFunctions.orderSectionType.DATES) {
      this.onDatesFormChange();
    }

    if (key === this.wdLocalSharedFunctions.orderSectionType.SHIPPING) {
      this.onShippingFormChange();
    }
  }

  /**
   * Update the list of state options
   * @since 4.41.0
   */
  updateStateOptions() {
    this.stateDropdownOptions = this.orderFormModel.delivery.address.country?.children;

    // Set the state to null if the country has been updated
    this.orderFormModel.delivery.address.state = null;
  }

  /**
   * Set the list of state options
   * @since 4.41.0
   */
  setStateOptions() {
    if (this.orderFormModel.delivery.address?.country?.id) {
      const orderFormCountry = this.orderFormModel.delivery.address.country.id;

      const selectedCountry = this.countryDropdownOptions.find(
        (country) => country.id === orderFormCountry,
      ) as any;

      this.stateDropdownOptions = selectedCountry?.children || [];
    }
  }

  /**
   * Cancel Edit Section
   */
  cancelEditSection(key: string) {
    // Revert Changes
    this.revertSectionState(key);
    this.changeDetector.detectChanges();
    // Reset State
    this.orderState[key].isChanges = false;
    this.orderState[key].isEditing = false;
    this.orderState[key].beforeChanges = null;
  }

  /**
   * Cancel Edit Section
   * @since 4.36.0
   */
  async openEditSection(key: string) {
    // Start loader
    this.orderState[key].isLoading = true;
    // Load required data (only when needed)
    if (key === this.wdLocalSharedFunctions.orderSectionType.DETAILS) {
      await this.wdManageOrdersService.fetchRequiredOrderDetailsData();
    }
    if (key === this.wdLocalSharedFunctions.orderSectionType.JOB) {
      await this.jobPhasesSearch(this.orderFormModel[key].job);
    }
    // Stop loader
    this.orderState[key].isLoading = false;
    // Set State
    this.orderState[key].isEditing = true;
    // Set Changes
    this.orderState[key].isChanges = false;
    this.saveSectionState(key);

    if (key === this.wdLocalSharedFunctions.orderSectionType.SHIPPING) {
      this.setDefaultDeliveryTime();
      this.setDefaultCountry();
    }
  }

  /**
   * Save Edit Section
   * @since 4.36.0
   */
  async saveEditSection(key: string) {
    // Start loader
    this.orderState[key].isSaving = true;
    try {
      // Update Order
      await this.wdOrdersService.updateOrder(
        key,
        this.orderFormModel,
        this.orderState,
      );
      // Reload Order (for Order Messages)
      void this.loadOrder();
      this.orderState[key].isChanges = false;
      this.orderState[key].isEditing = false;
    } catch (e) {
      // Revert Changes on Error
      this.revertSectionState(key);
    }
    // Stop loader / Set State
    this.orderState[key].isSaving = false;
  }

  /**
   * Open order item hole deitals modal
   */
  openFittingHoleDetailsModal(element: any) {
    this.matDialog.open(OrderItemHolesComponent, {
      data: element,
    });
  }

  /**
   * Filter Messages based on Selection
   * @since 4.36.0
   */
  filterMessages() {
    this.orderFormModel[
      this.wdLocalSharedFunctions.orderSectionType.MESSAGES
    ].valuesDisplayed = this.orderFormModel[
      this.wdLocalSharedFunctions.orderSectionType.MESSAGES
    ].valuesUnfiltered.filter((message: any) => {
      const showTransactionMessages =
        this.wdOrderMessageService.showTransactionMessages &&
        message.type === 'transaction';
      const showUserMessages =
        this.wdOrderMessageService.showUserMessages && message.type === 'user';
      return showTransactionMessages || showUserMessages;
    });
  }

  /**
   * Initialize order form model according to the action mode [edit|add]
   */
  initializeOrderFormModel(order: IRawInputOrder): IOrderFormModel {
    const isEdit = !!order;
    const assembly = isEdit ? order.assembly : null;
    const assemblyId = assembly ? order.assembly.id : null;
    const assemblyLink = assembly
      ? this.wdEnvironmentService.resources.getAssemblyViewUrl(assemblyId)
      : null;

    const orderedFor = isEdit ? order.orderedFor : null;
    const orderedForCompany = orderedFor ? orderedFor.company : null;
    const orderedForCompanyLabel = orderedForCompany
      ? orderedForCompany.label
      : null;

    // Get order job details
    const orderJob = this.wdSharedFunctionsService.getDescendantProperty(
      order,
      'job',
    );
    const orderJobPhase = this.wdSharedFunctionsService.getDescendantProperty(
      order,
      'job.phase',
    );
    // Set order job phase labelWithCode field (if phase exists)
    if (orderJobPhase) {
      const phaseCode = orderJobPhase.code;
      const phaseLabel = orderJobPhase.label;

      if (phaseCode && phaseLabel) {
        // Display concat phase code and label
        orderJobPhase.labelWithCode = `${phaseCode} - ${phaseLabel}`;
      } else {
        // Display error
        orderJobPhase.labelWithCode = this.wdTranslate.instant(
          'errors.missingPhaseCodeOrLabel',
        );
      }
    }

    // Set order job details
    const job = {
      job: isEdit ? orderJob : null,
      phase: isEdit ? orderJobPhase : null,
    };

    // Set the showShippingDetailsContainer flag
    const orderShippingOption = order.delivery.option.value;
    this.setShowShippingDetailsContainerFlag(
      (orderShippingOption === OrderShippingOptions.JOBSITE &&
        order.job.delivery) ||
        orderShippingOption === OrderShippingOptions.ADDRESS,
    );

    if (
      [OrderShippingOptions.ADDRESS, OrderShippingOptions.JOBSITE].includes(
        orderShippingOption,
      )
    ) {
      if (
        !order.delivery.address ||
        orderShippingOption === OrderShippingOptions.JOBSITE
      ) {
        const jobAddress =
          orderShippingOption === OrderShippingOptions.JOBSITE &&
          order.job.delivery
            ? order.job.delivery
            : null;
        // create an empty address form or use the order job address if it exists and the shipping option is 'jobSite'
        order.delivery.address = {
          city: jobAddress?.city ? jobAddress.city : null,
          company: jobAddress?.company?.name ? jobAddress.company.name : null,
          country: {
            id: jobAddress?.country?.code ? jobAddress.country.code : null,
            label: jobAddress?.country?.label ? jobAddress.country.label : null,
          },
          phone: jobAddress?.phone ? jobAddress.phone : null,
          state: {
            id: jobAddress?.state?.code ? jobAddress.state.code : null,
            label: jobAddress?.state?.label ? jobAddress.state.label : null,
          },
          street: jobAddress?.address ? jobAddress.address : null,
          zipcode: jobAddress?.postalCode ? jobAddress.postalCode : null,
          instructions: jobAddress?.instructions
            ? jobAddress.instructions
            : null,
        };
      }

      let result = null;
      const countryCode = order.delivery.address?.country?.id;
      if (countryCode) {
        result = this.countryDropdownOptions.find(
          (option) => option.id === countryCode,
        );
      }
      // Set the country code (if applicable)
      if (result) {
        order.delivery.address.country = result;
      }
    }

    // Default values
    let startTime = {
      objectValue: {},
      readValue: 'N/A',
    };
    let endTime = {
      objectValue: {},
      readValue: 'N/A',
    };

    if (order.delivery?.time) {
      // todo: to be updated when the API work is done
      const timeFormatOptions: any = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      };
      const startDate = new Date(order.delivery.time.start);
      const endDate = new Date(order.delivery.time.end);
      startTime = {
        objectValue: {
          hour: startDate.getHours(),
          minute: startDate.getMinutes(),
          second: 0,
        },
        readValue: startDate.toLocaleTimeString([], timeFormatOptions),
      };
      endTime = {
        objectValue: {
          hour: endDate.getHours(),
          minute: endDate.getMinutes(),
          second: 0,
        },
        readValue: endDate.toLocaleTimeString([], timeFormatOptions),
      };
    }

    if (order.formFields) {
      const currentUser = this.wdUsersService.currentUser.value;

      // Get the current user's user group uids
      const userGroupUids = currentUser?.tags?.groups.map(
        (group: { uid: string }) => group.uid,
      );

      // Filter out the Form Fields that do not have matching user group uids with the current user
      order.formFields = order.formFields.filter(
        (formField: IOrderFormFieldsModel) => {
          if (formField.userGroups && formField.userGroups.length > 0) {
            // Get all the formField's user group uids into an array
            const formFieldUserGroupUids = formField.userGroups.map(
              (userGroup: { uid: string }) => userGroup.uid,
            );

            if (formFieldUserGroupUids.length > 0) {
              // check if any of the user's user group matches the form field's userGroups
              return userGroupUids.some((uid: string) =>
                formFieldUserGroupUids.includes(uid),
              );
            }
          } else {
            // return true if there is no user group uid requirement for the form field
            return true;
          }
        },
      );
    }

    return {
      assembly: {
        id: assemblyId,
        link: assemblyLink,
      },
      attachments: isEdit ? order.attachments : [],
      calculations: {
        fabrication: isEdit
          ? order.calculations?.totals?.current?.shopLabour
          : null,
        weight: isEdit ? order.calculations?.totals?.current?.weight : null,
        price: isEdit ? order.calculations?.totals?.current?.price : null,
      },
      details: {
        id: isEdit ? order.id : null,
        organization: orderedForCompanyLabel,
        index: isEdit ? order.index : null,
        number: isEdit ? order.number : null,
        orderedBy: isEdit ? order.orderedBy : null,
        orderedFor: orderedFor,
        measuredBy: isEdit ? order.measuredBy : null,
        tag: isEdit ? order.tag : null,
        userGroup: isEdit ? order.userGroup : null,
        poNumber: isEdit ? order.poNumber : null,
      },
      dates: {
        ordered: isEdit ? order.ordered : null,
        requested: isEdit ? order.requested : null,
        productionStart: isEdit ? order.production?.date : null,
        productionEnd: isEdit
          ? order.production?.endDate || order.production?.date
          : null,
        delivery: isEdit ? order.delivery?.date : null,
      },
      delivery: {
        address: isEdit ? order.delivery?.address : null,
        option: isEdit ? order.delivery?.option : null,
        loadingZone: isEdit ? order.delivery?.loadingZone : null,
        vehicle: isEdit ? order.delivery?.vehicle : null,
        startTime,
        endTime,
      },
      downloads: isEdit ? order.downloads : [],
      exports: isEdit ? order.exports : [],
      formFields: order.formFields,
      fulfillment: {
        groups: isEdit ? order.fulfillment?.groups : [],
      },
      instructions: isEdit ? order.specialInstructions : null,
      job: job,
      messages: {
        valuesDisplayed: isEdit ? (order.messages ? order.messages : []) : [],
        valuesUnfiltered: isEdit ? (order.messages ? order.messages : []) : [],
      },
      modelLink: isEdit ? order.modelLink : null,
      reports: isEdit ? order.reports : [],
      statuses: {
        overall: isEdit ? order.status : null,
        production: isEdit ? order.production?.status : null,
        shipping: isEdit ? order.delivery?.status : null,
      },
      type: isEdit ? order.type : null,
      isLookahead: order.lookahead && order.lookahead.isLookaheadOrder,
      isEcommerce: order.type === 'ecommerce',
      isModelViewerEnabled: this.isModelViewerEnabled(order),
    };
  }

  /**
   * Is called by the view and returns whether the number of
   * selected rows matches the total number of rows.
   *
   * @since 4.35.0
   * @memberof ManageComponent
   */
  isAllSelected(dataSource: any, selections: any): boolean {
    let result = false;
    if (dataSource && selections) {
      const numSelected = selections.selected.length;
      const numRows = dataSource.data.length;
      result = numSelected === numRows;
    }
    return result;
  }

  /**
   * Is Model Viewer Enabled
   * @since 4.61.0
   */
  isModelViewerEnabled(order: IRawInputOrder) {
    let result = false;

    // Return true when the order has an attachment with a .dwg file
    const attachments = order.attachments;
    if (attachments && Array.isArray(attachments)) {
      attachments.forEach(function (item) {
        if (item.type === 'image/vnd.dwg') {
          result = true;
        }
      });
    }

    // Return true if the order is a digital spooling assembly
    const vendor = order.source?.vendor;
    if (vendor && vendor.name === 'bcx.digitalspooling') {
      result = true;
    }

    return result;
  }

  /**
   * Manage Adjustment
   * @since 4.36.0
   */
  manageAdjustment(group?: any, adjustment?: any) {
    // Open dialog
    this.matDialog.open(OrderAdjustmentsComponent, {
      disableClose: true,
      width: '475px',
      data: {
        adjustment: adjustment,
        group: group,
        order: {
          index: this.orderIndex,
          number: this.orderFormModel.details.number,
        },
        onSuccessCallback: () => {
          // Reload Order Data
          this.loadOrder();
        },
      },
    });
  }

  /**
   * Export order item holes data report
   */
  exportHoleData(group: any): void {
    this.wdOrderItemHolesService.downloadHoleDataReport(group, {
      title: this.orderTitle,
    });
  }

  /**
   * Selects all rows if they are not all selected; otherwise clear selection.
   *
   * @since 4.35.0
   * @memberof ManageComponent
   */
  masterToggle(dataSource: any, selections: any): void {
    this.isAllSelected(dataSource, selections)
      ? selections.clear()
      : dataSource.data.forEach((row: any) => {
          selections.select(row);
        });
  }

  /**
   * On Destroy
   * @since 4.26.0
   */
  ngOnDestroy() {
    // Unsubscribe from Changes
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  /**
   * @since 4.26.0
   */
  ngOnInit() {
    this.watchForRouteChange();
    // Set Data
    this.setData();
    // Watch Form Changes
    this.datesForm?.valueChanges?.subscribe(() => {
      this.checkForValueChanges(
        this.wdLocalSharedFunctions.orderSectionType.DATES,
      );
    });
    this.orderDetailsForm?.valueChanges?.subscribe(() => {
      this.checkForValueChanges(
        this.wdLocalSharedFunctions.orderSectionType.DETAILS,
      );
    });
    this.jobForm?.valueChanges?.subscribe(() => {
      this.checkForValueChanges(
        this.wdLocalSharedFunctions.orderSectionType.JOB,
      );
    });
    this.specialInstructionsForm?.valueChanges?.subscribe(() => {
      this.checkForValueChanges(
        this.wdLocalSharedFunctions.orderSectionType.INSTRUCTIONS,
      );
    });
    this.statusesForm?.valueChanges?.subscribe(() => {
      this.checkForValueChanges(
        this.wdLocalSharedFunctions.orderSectionType.STATUSES,
      );
    });
    this.shippingDetailsForm?.valueChanges?.subscribe(() => {
      this.checkForValueChanges(
        this.wdLocalSharedFunctions.orderSectionType.SHIPPING,
      );
    });
    this.formFieldsForm?.valueChanges?.subscribe(() => {
      this.checkForValueChanges(
        this.wdLocalSharedFunctions.orderSectionType.FORMFIELDS,
      );
    });
  }

  /**
   * Subscribe to current route
   *
   * @since 4.63.0
   *
   * @memberof ManageComponent
   */
  private watchForRouteChange(): void {
    // Subscribe to Route Change
    const routeSubscription = this.activatedRoute.params.subscribe(
      async (params) => {
        this.orderIndex = params.orderIndex;
      },
    );
    this.subscriptions.add(routeSubscription);
  }

  /**
   * Files Change Event
   * @since 4.36.0
   */
  onFilesSelected(event: any) {
    const files = event.target.files;
    // Set Pending Files
    this.uploadQueue = this.wdFileUploadService.setUploadQueue(files);
    if (this.uploadQueue.length > 0) {
      // Open Attachments Dialog
      this.matDialog.open(ManageAttachmentsComponent, {
        disableClose: true,
        width: '450px',
        data: {
          orderIndex: this.orderIndex,
          files: this.uploadQueue,
          isPending: true,
          onCancelCallback: () => {
            this.uploadQueue = []; // Clear selected pending files
          },
          onSuccessCallback: () => {
            // Reload Order Data
            void this.loadOrder();
          },
        },
      });
    }
  }

  /**
   * On Messages Changes
   * @since 4.36.0
   */
  onMessagesChanges() {
    // Reload Order Data (Includes Messages)
    // @todo Future Refactor, reload only messages to be more efficient
    void this.loadOrder();
  }

  /**
   * Revert the section to the last known state before changes.
   */
  revertSectionState(key: string) {
    const lastKnownState = this.orderState[key].beforeChanges;
    if (lastKnownState) {
      // Parse last known state from JSON
      const parsedLastState = JSON.parse(
        lastKnownState,
        WdLocalSharedFunctionsService.revertSectionDateReviver,
      );
      // Handle anything we don't want to revert
      if (key === this.wdLocalSharedFunctions.orderSectionType.SHIPPING) {
        parsedLastState.vehicle = this.orderFormModel[key].vehicle;
      }
      // Revert order form model section
      this.orderFormModel[key] = parsedLastState;
    } else {
      // Set state to undefined
      this.orderFormModel[key] = undefined;
    }
  }

  /**
   * Run date validations
   * @since 4.59.0
   */
  onDatesFormChange(): void {
    const {
      dates: { productionStart, productionEnd, delivery },
    } = this.orderFormModel;
    const productionStartDate = productionStart.getTime();
    const productionEndDate =
      productionEnd?.getTime() || productionStart.getTime();
    const shippingDate = delivery?.getTime();

    this.isProductionDateRangeValid = productionStartDate <= productionEndDate;
    this.isProductionEndBeforeShipping = productionEndDate <= shippingDate;
  }

  /**
   * Run shipping validations
   * @since 4.62.0
   */
  onShippingFormChange(): void {
    const {
      delivery: { startTime, endTime },
    } = this.orderFormModel;

    this.isDeliveryWindowValid =
      (startTime.objectValue === null && endTime.objectValue === null) ||
      startTime?.objectValue?.hour < endTime?.objectValue?.hour;
  }

  /**
   * Save a copy of the section before changes
   * @since 4.36.0
   */
  saveSectionState(key: string) {
    this.orderState[key].beforeChanges = null;
    if (this.orderFormModel[key]) {
      this.orderState[key].beforeChanges = JSON.stringify(
        this.orderFormModel[key],
      );
    }
  }

  /**
   * Get and set a list of active fulfillment group indexes for an order
   * Group is active if it contains adjustments, fittings or products
   * @since 4.48.0
   */
  setActiveFulfillmentGroupIndexes(order: any) {
    // Active indexes based on group status
    if (order.fulfillment?.groups) {
      this.activeFulfillmentGroupIndexes = order.fulfillment?.groups
        .filter((group: any) => {
          const hasAdjustments = group.adjustmentsDataSource?.data?.length > 0;
          const hasFittings = group.fittingsDataSource?.data?.length > 0;
          const hasProducts = group.productsDataSource?.data?.length > 0;
          const hasTrackerItems =
            group.trackerItemsDataSource?.data?.length > 0;

          return (
            hasAdjustments || hasFittings || hasProducts || hasTrackerItems
          );
        })
        .map((group: any) => {
          return group.index;
        });
    }
  }

  /**
   * The label for the checkbox on the passed row
   *
   * @since 4.35.0
   * @memberof ManageComponent
   */
  setCheckboxLabel(row: any, dataSource: any, selections: any): string {
    if (!row) {
      return `${
        this.isAllSelected(dataSource, selections) ? 'select' : 'deselect'
      } all`;
    }
    return `${selections.isSelected(row) ? 'deselect' : 'select'} row ${
      row.position + 1
    }`;
  }

  /**
   * Set the default country value
   *
   * @since 4.66.0
   * @memberof ManageComponent
   */
  setDefaultCountry() {
    if (this.orderFormModel.delivery.address) {
      // Get stored country code
      let countryCode = this.orderFormModel.delivery.address?.country?.id;
      // Use default country code if none saved
      if (!countryCode) {
        countryCode = this.wdOrderShippingGeolocationsService
          .defaultCountryCode;
      }
      // Find the matching country code
      const result = this.countryDropdownOptions.find(
        (option) => option.id === countryCode,
      );
      // Set the country code (if applicable)
      if (result) {
        this.orderFormModel.delivery.address.country = result;
        this.setStateOptions();
      }
    }
  }

  /**
   * Set the default delivery time window values
   *
   * @since 4.62.0
   * @memberof ManageComponent
   */
  setDefaultDeliveryTime() {
    const startTimeObject = this.orderFormModel[
      this.wdLocalSharedFunctions.orderSectionType.SHIPPING
    ].startTime.objectValue;
    const endTimeObject = this.orderFormModel[
      this.wdLocalSharedFunctions.orderSectionType.SHIPPING
    ].endTime.objectValue;

    if (Object.keys(startTimeObject).length === 0) {
      this.orderFormModel[
        this.wdLocalSharedFunctions.orderSectionType.SHIPPING
      ].startTime.objectValue = {
        hour: 6,
        minute: 0,
        second: 0,
      };
      this.orderState[
        this.wdLocalSharedFunctions.orderSectionType.SHIPPING
      ].isChanges = true;
    }
    if (Object.keys(endTimeObject).length === 0) {
      this.orderFormModel[
        this.wdLocalSharedFunctions.orderSectionType.SHIPPING
      ].endTime.objectValue = {
        hour: 8,
        minute: 0,
        second: 0,
      };
      this.orderState[
        this.wdLocalSharedFunctions.orderSectionType.SHIPPING
      ].isChanges = true;
    }
  }

  /**
   * Open vehicle assignments modal.
   */
  openVehicleAssignmentsModal(order: IOrderFormModel) {
    // Open dialog
    const dialogRef = this.matDialog.open(OrderAssignmentsComponent, {
      width: '400px',
      maxWidth: '90vw',
      data: {
        orderId: order.details.id,
        orderNumber: order.details.number,
        assignments: order.delivery.vehicle.assignments,
        assignmentOptions: order.delivery.vehicle.assignmentOptions,
      },
    });
    // Capture the result
    dialogRef.afterClosed().subscribe(async (result: boolean) => {
      if (result) {
        // Reload vehicle assignments
        await this.getOrderShippingVehicleAssignments();
        // Reload order data
        void this.loadOrder();
      }
    });
  }

  /**
   * On Blur Job Typeahead
   * @since 4.37.0
   */
  typeaheadJobBlur(model: any) {
    // Clear value if no model selected
    if (!model?.index) {
      this.orderFormModel[
        this.wdLocalSharedFunctions.orderSectionType.JOB
      ].job = null;
    }
  }

  /**
   * Set the showShippingDetailsContainer flag with the given value
   * - If set to true then the shipping details container will display
   * @since 4.74.0
   */
  setShowShippingDetailsContainerFlag(value: boolean) {
    this.showShippingDetailsContainer = value;
  }
}
